import type { FC } from 'react';
import React, { useCallback, useRef } from 'react';
import type { ListActivePaymentDto, PaymentMethodDto } from '../../../../../../generated-api';
import { PaymentState } from '../../../../../../generated-api';
import classNames from 'clsx';
import CashButton from './CashButton';
import IntegratedTerminalPaymentButton from './IntegratedTerminalPaymentButton';
import UnintegratedPaymentButton from './UnintegratedPaymentButton';
import { isPaymentInProgress, useTerminalStatus, useTrackedPayment } from '../../../../../context/TerminalStore';
import { useF1NetworkState } from '../../../../../context/F1NetworkStateStore';
import { useFocusedPaymentStore } from '../../../../../context/FocusedPaymentStore';
import useDeviceId from '../../../../../hooks/useDeviceId';

interface Props {
  paymentMethod: PaymentMethodDto;
  activePayment?: ListActivePaymentDto;
  initiatePayment: (
    paymentMethod: PaymentMethodDto,
    dynamicFields?: Record<string, string>,
    promptTips?: boolean
  ) => void;
  shouldGrow: boolean;
  canTryPaymentAnyway: boolean;
}

const PaymentButton: FC<Props> = ({
  paymentMethod,
  activePayment,
  initiatePayment,
  shouldGrow,
  canTryPaymentAnyway,
}) => {
  const isCash = paymentMethod.paymentMethod === 'CASH';
  const terminalState = useTerminalStatus(
    paymentMethod.id,
    activePayment?.paymentState === PaymentState.AWAITING_PAYMENT
  );
  const trackedPayment = useTrackedPayment(paymentMethod.id);
  const { isOffline } = useF1NetworkState();
  const deviceId = useDeviceId();

  const className = classNames({
    'bg-grey-medium text-grey-dark border-grey-medium cursor-not-allowed': terminalState === 'OFFLINE' || isOffline,
    'flex-1': shouldGrow && !isCash,
    'flex-col ml-2 first-of-type:ml-4 last-of-type:mr-4': true,
    'flex-none': !shouldGrow,
    'opacity-75': terminalState === 'BUSY',
  });
  const pendingRef = useRef(false);
  const handleButtonClick = useCallback(
    (ignoreTerminalState = false) => {
      if (isOffline) {
        return;
      }

      if (activePayment?.paymentState === PaymentState.AWAITING_PAYMENT) {
        useFocusedPaymentStore.getState().setFocusedPayment(activePayment);
        return;
      }

      if (trackedPayment && isPaymentInProgress(trackedPayment.state)) {
        useFocusedPaymentStore.getState().setFocusedPayment({
          cart: trackedPayment.cart,
          deviceId,
          paymentMethod,
          paymentReference: trackedPayment.paymentReference,
          paymentState: PaymentState.AWAITING_PAYMENT,
        });
        return;
      }

      if (pendingRef.current) {
        return;
      }

      if (!ignoreTerminalState && terminalState !== 'AVAILABLE') {
        return;
      }

      pendingRef.current = true;
      setTimeout(() => (pendingRef.current = false), 500);

      initiatePayment(paymentMethod, undefined, true);
    },
    [activePayment, deviceId, initiatePayment, isOffline, paymentMethod, terminalState, trackedPayment]
  );

  if (isCash) {
    return (
      <CashButton
        className={className}
        paymentMethod={paymentMethod}
        handleButtonClick={handleButtonClick}
        activePayment={activePayment}
        busy={terminalState === 'BUSY'}
      />
    );
  }
  if (paymentMethod.provider === 'UNINTEGRATED') {
    return (
      <UnintegratedPaymentButton
        paymentMethod={paymentMethod}
        onClick={initiatePayment}
        className={className}
        busy={terminalState === 'BUSY'}
      />
    );
  }

  return (
    <IntegratedTerminalPaymentButton
      paymentMethod={paymentMethod}
      terminalState={terminalState}
      className={className}
      onClick={() => handleButtonClick()}
      onTryAnyway={() => handleButtonClick(true)}
      canTryPaymentAnyway={canTryPaymentAnyway}
    />
  );
};

export default React.memo(PaymentButton);
